import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import axios from "axios";
import {
  Globe,
  Shield,
  ShieldOff,
  Plus,
  Trash2,
  MapPin,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  Radio,
  FileText
} from "lucide-react";
import { BlockedPageWorkspace } from "@/components/geo/BlockedPageWorkspace";
import { blockedPageDefaults } from "@/lib/blockedPagePreview";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GeoAccess = () => {
  const { token } = useAuth();
  const [config, setConfig] = useState({
    whitelisted_ips: [],
    blacklisted_ips: [],
    allowed_countries: [],
    blocked_os: []
  });
  const [loading, setLoading] = useState(true);
  const [newIP, setNewIP] = useState("");
  const [newCountry, setNewCountry] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null); // null | 'syncing' | 'synced' | 'failed'
  const [syncDetail, setSyncDetail] = useState("");
  const [activeTab, setActiveTab] = useState("rules");
  const [blockedPage, setBlockedPage] = useState(blockedPageDefaults);
  const [blockedPageLoading, setBlockedPageLoading] = useState(true);
  const [blockedPageSaving, setBlockedPageSaving] = useState(false);
  const [blockedPageMessage, setBlockedPageMessage] = useState({ type: "", text: "" });

  const fetchConfig = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/geo/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfig(response.data);
    } catch (error) {
      console.error("Error fetching geo config:", error);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    fetchConfig();
    const interval = setInterval(fetchConfig, 15000);
    return () => clearInterval(interval);
  }, [fetchConfig]);

  const fetchBlockedPage = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/user/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBlockedPage({ ...blockedPageDefaults, ...(response.data.blocked_page || {}) });
    } catch (error) {
      setBlockedPageMessage({ type: "error", text: error.response?.data?.detail || "Blocked-page settings could not be loaded." });
    } finally {
      setBlockedPageLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchBlockedPage();
  }, [fetchBlockedPage]);

  const saveBlockedPage = async () => {
    setBlockedPageSaving(true);
    setBlockedPageMessage({ type: "", text: "" });
    try {
      const response = await axios.put(`${API}/user/settings`, { blocked_page: blockedPage }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data?.config?.blocked_page) {
        setBlockedPage({ ...blockedPageDefaults, ...response.data.config.blocked_page });
      }
      setBlockedPageMessage({ type: "success", text: "Blocked page saved and syncing to your domains." });
    } catch (error) {
      setBlockedPageMessage({ type: "error", text: error.response?.data?.detail || "Failed to save blocked page." });
    } finally {
      setBlockedPageSaving(false);
    }
  };

  const handleIPAction = async (ip, action) => {
    setActionLoading(true);
    setSyncStatus("syncing");
    setSyncDetail(`${action === "whitelist" ? "Whitelisting" : action === "blacklist" ? "Blacklisting" : "Removing"} ${ip}`);
    try {
      const response = await axios.post(
        `${API}/geo/ip`,
        { ip, action },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setConfig({
        ...config,
        whitelisted_ips: response.data.config.whitelisted_ips || [],
        blacklisted_ips: response.data.config.blacklisted_ips || []
      });
      setNewIP("");
      setSyncStatus("synced");
      setSyncDetail(`IP ${ip} — saved & synced to agents`);
    } catch (error) {
      console.error("Error managing IP:", error);
      setSyncStatus("failed");
      setSyncDetail(`Failed to ${action} IP ${ip}`);
    }
    setActionLoading(false);
    setTimeout(() => setSyncStatus(null), 4000);
  };

  const handleOSAction = async (os_name, action) => {
    setActionLoading(true);
    setSyncStatus("syncing");
    setSyncDetail(`${action === "add" ? "Blocking" : "Unblocking"} ${os_name}`);
    try {
      const response = await axios.post(
        `${API}/geo/os`,
        { os_name, action },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setConfig({
        ...config,
        blocked_os: response.data.config.blocked_os || []
      });
      setSyncStatus("synced");
      setSyncDetail(`OS ${os_name} — saved & synced to agents`);
    } catch (error) {
      console.error("Error managing OS:", error);
      setSyncStatus("failed");
      setSyncDetail(`Failed to ${action} OS ${os_name}`);
    }
    setActionLoading(false);
    setTimeout(() => setSyncStatus(null), 4000);
  };

  const handleCountryAction = async (country, action) => {
    setActionLoading(true);
    setSyncStatus("syncing");
    setSyncDetail(`${action === "add" ? "Allowing" : "Removing"} ${country}`);
    try {
      const response = await axios.post(
        `${API}/geo/country`,
        { country, action },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setConfig({
        ...config,
        allowed_countries: response.data.config.allowed_countries || []
      });
      setNewCountry("");
      setSyncStatus("synced");
      setSyncDetail(`Country ${country} — saved & synced to agents`);
    } catch (error) {
      console.error("Error managing country:", error);
      setSyncStatus("failed");
      setSyncDetail(`Failed to ${action} country ${country}`);
    }
    setActionLoading(false);
    setTimeout(() => setSyncStatus(null), 4000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1750px] space-y-6">
      {/* Header */}
      <div className="border-b border-slate-800 pb-5">
        <p className="text-xs font-semibold uppercase text-cyan-400">Traffic policy</p>
        <h1 className="mt-1 text-3xl font-orbitron font-bold text-white" data-testid="geo-title">Geo Access Control</h1>
        <p className="mt-2 text-sm text-slate-400">Manage who can reach your links and what rejected visitors see.</p>
      </div>

      <nav className="flex w-full gap-1 overflow-x-auto border-b border-slate-800" data-testid="geo-tabs" aria-label="Geo Access workspaces">
        <button type="button" onClick={() => setActiveTab("rules")} className={`flex min-w-fit items-center gap-2 border-b-2 px-4 py-3 text-sm font-semibold transition-colors ${activeTab === "rules" ? "border-cyan-400 text-cyan-200" : "border-transparent text-slate-500 hover:text-slate-200"}`} data-testid="tab-rules" aria-selected={activeTab === "rules"}><Shield className="h-4 w-4" />Security Rules</button>
        <button type="button" onClick={() => setActiveTab("blocked-page")} className={`flex min-w-fit items-center gap-2 border-b-2 px-4 py-3 text-sm font-semibold transition-colors ${activeTab === "blocked-page" ? "border-red-400 text-red-200" : "border-transparent text-slate-500 hover:text-slate-200"}`} data-testid="tab-blocked-page" aria-selected={activeTab === "blocked-page"}><FileText className="h-4 w-4" />Blocked Page</button>
      </nav>

      {activeTab === "rules" && <>

      {/* Sync Status Indicator */}
      {syncStatus && (
        <div
          data-testid="sync-status-banner"
          className={`flex items-center gap-3 rounded-md border px-4 py-3 transition-[background-color,border-color,color] duration-300 ${
            syncStatus === "syncing"
              ? "bg-cyan-500/10 border-cyan-500/30 text-cyan-300"
              : syncStatus === "synced"
              ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300"
              : "bg-red-500/10 border-red-500/30 text-red-300"
          }`}
        >
          {syncStatus === "syncing" && (
            <RefreshCw className="w-4 h-4 animate-spin flex-shrink-0" />
          )}
          {syncStatus === "synced" && (
            <Radio className="w-4 h-4 flex-shrink-0" />
          )}
          {syncStatus === "failed" && (
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          )}
          <span className="text-sm font-medium" data-testid="sync-status-text">{syncDetail}</span>
          {syncStatus === "syncing" && (
            <span className="text-xs opacity-70 ml-auto">Pushing to agents...</span>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* IP Management */}
        <div className="space-y-6">
          {/* Add IP Form */}
          <div className="card-dark p-6" data-testid="ip-form">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-cyan-400" />
              Manage IP Addresses
            </h3>
            <div className="flex gap-2 mb-4">
              <input
                type="text"
                placeholder="Enter IP address (e.g., 192.168.1.1)"
                value={newIP}
                onChange={(e) => setNewIP(e.target.value)}
                className="input-dark flex-1"
                data-testid="ip-input"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handleIPAction(newIP, "whitelist")}
                disabled={!newIP || actionLoading}
                className="btn-primary flex-1 flex items-center justify-center gap-2"
                data-testid="whitelist-btn"
              >
                <CheckCircle className="w-4 h-4" />
                Whitelist
              </button>
              <button
                onClick={() => handleIPAction(newIP, "blacklist")}
                disabled={!newIP || actionLoading}
                className="btn-danger flex-1 flex items-center justify-center gap-2"
                data-testid="blacklist-btn"
              >
                <AlertTriangle className="w-4 h-4" />
                Blacklist
              </button>
            </div>
          </div>

          {/* Whitelisted IPs */}
          <div className="card-dark p-6" data-testid="whitelist-section">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-400" />
              Whitelisted IPs
              <span className="badge-success ml-2">{config.whitelisted_ips.length}</span>
            </h3>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {config.whitelisted_ips.length === 0 ? (
                <p className="text-slate-500 text-sm">No whitelisted IPs</p>
              ) : (
                config.whitelisted_ips.map((ip) => (
                  <div
                    key={ip}
                    className="flex items-center justify-between p-3 bg-green-500/5 border border-green-500/20 rounded-lg"
                  >
                    <span className="font-mono text-sm text-slate-300">{ip}</span>
                    <button
                      onClick={() => handleIPAction(ip, "remove_whitelist")}
                      className="text-slate-400 hover:text-red-400 transition-colors"
                      data-testid={`remove-whitelist-${ip}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Blacklisted IPs */}
          <div className="card-dark p-6" data-testid="blacklist-section">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <ShieldOff className="w-5 h-5 text-red-400" />
              Blacklisted IPs
              <span className="badge-danger ml-2">{config.blacklisted_ips.length}</span>
            </h3>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {config.blacklisted_ips.length === 0 ? (
                <p className="text-slate-500 text-sm">No blacklisted IPs</p>
              ) : (
                config.blacklisted_ips.map((ip) => (
                  <div
                    key={ip}
                    className="flex items-center justify-between p-3 bg-red-500/5 border border-red-500/20 rounded-lg"
                  >
                    <span className="font-mono text-sm text-slate-300">{ip}</span>
                    <button
                      onClick={() => handleIPAction(ip, "remove_blacklist")}
                      className="text-slate-400 hover:text-red-400 transition-colors"
                      data-testid={`remove-blacklist-${ip}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Country Management */}
        <div className="space-y-6">
          {/* Add Country Form */}
          <div className="card-dark p-6" data-testid="country-form">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Globe className="w-5 h-5 text-cyan-400" />
              Manage Country Access
            </h3>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Enter country (e.g., US or United States)"
                value={newCountry}
                onChange={(e) => setNewCountry(e.target.value)}
                className="input-dark flex-1"
                data-testid="country-input"
              />
              <button
                onClick={() => handleCountryAction(newCountry, "add")}
                disabled={!newCountry || actionLoading}
                className="btn-primary flex items-center gap-2"
                data-testid="allow-country-btn"
              >
                <Plus className="w-4 h-4" />
                Allow
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Note: If no countries are specified, all countries are allowed by default.
            </p>
          </div>

          {/* Allowed Countries */}
          <div className="card-dark p-6" data-testid="countries-section">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-cyan-400" />
              Allowed Countries
              <span className="badge-neutral ml-2">{config.allowed_countries.length}</span>
            </h3>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {config.allowed_countries.length === 0 ? (
                <div className="text-center py-8">
                  <Globe className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-500 text-sm">No country restrictions</p>
                  <p className="text-slate-600 text-xs">All countries are currently allowed</p>
                </div>
              ) : (
                config.allowed_countries.map((country) => (
                  <div
                    key={country}
                    className="flex items-center justify-between p-3 bg-slate-800/50 border border-slate-700/50 rounded-lg"
                  >
                    <span className="text-sm text-slate-300">{country}</span>
                    <button
                      onClick={() => handleCountryAction(country, "remove")}
                      className="text-slate-400 hover:text-red-400 transition-colors"
                      data-testid={`remove-country-${country}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* OS Restrictions */}
          <div className="card-dark p-6" data-testid="os-section">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-cyan-400" />
              Blocked Operating Systems
              <span className="badge-neutral ml-2">{config.blocked_os?.length || 0}</span>
            </h3>
            
            <p className="text-xs text-slate-400 mb-4">
              Toggle which Operating Systems to explicitly block. If none are selected (default), all OS traffic is allowed.
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {[
                { id: "windows", name: "Windows", icon: <svg className="w-5 h-5 text-blue-400" viewBox="0 0 24 24" fill="currentColor"><path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-12.9-1.801"/></svg> },
                { id: "mac", name: "macOS", icon: <svg className="w-5 h-5 text-slate-200" viewBox="0 0 24 24" fill="currentColor"><path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.72.806-1.376 2.273-1.209 3.637 1.352.104 2.649-.611 3.496-1.625z"/></svg> },
                { id: "linux", name: "Linux", icon: "🐧" },
                { id: "ios", name: "iOS", icon: <svg className="w-5 h-5 text-slate-200" viewBox="0 0 24 24" fill="currentColor"><path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.72.806-1.376 2.273-1.209 3.637 1.352.104 2.649-.611 3.496-1.625z"/></svg> },
                { id: "android", name: "Android", icon: <svg className="w-5 h-5 text-green-500" viewBox="0 0 24 24" fill="currentColor"><path d="M17.523 15.3414c-.5511 0-.9993-.4486-.9993-.9997s.4482-.9993.9993-.9993c.5511 0 .9993.4482.9993.9993.0004.5511-.4482.9997-.9993.9997m-11.046 0c-.5511 0-.9993-.4486-.9993-.9997s.4482-.9993.9993-.9993c.5511 0 .9993.4482.9993.9993 0 .5511-.4482.9997-.9993.9997m11.4045-6.02l1.9973-3.4592c.1146-.1998.0454-.454-.1544-.569-.199-.1151-.4536-.0458-.569.1536l-2.016 3.4912c-1.4131-.6471-2.9839-1.0101-4.6394-1.0101-1.6559 0-3.2263.363-4.6394 1.0101l-2.016-3.4912c-.1151-.1994-.3696-.2687-.569-.1536-.1998.115-.269.3692-.1544.569l1.9973 3.4592C4.303 10.909 2.053 14.1568 2 17.9622h20c-.053-3.8054-2.303-7.0532-5.1185-8.6408"/></svg> },
              ].map((os) => {
                const isBlocked = config.blocked_os?.includes(os.id);
                return (
                  <button
                    key={os.id}
                    onClick={() => handleOSAction(os.id, isBlocked ? "remove" : "add")}
                    disabled={actionLoading}
                    className={`flex h-24 flex-col items-center justify-center gap-2 rounded-md border p-3 transition-[background-color,border-color,color] ${
                      isBlocked
                        ? "bg-red-500/10 border-red-500/50 text-red-400"
                        : "bg-slate-800/50 border-slate-700/50 text-slate-400 hover:border-slate-600 hover:text-white"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{os.icon}</span>
                      <span className="text-sm font-medium">{os.name}</span>
                    </div>
                    {isBlocked ? (
                      <span className="text-[10px] uppercase tracking-wider font-bold bg-red-500/20 px-2 py-0.5 rounded text-red-400">Blocked</span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-wider font-medium text-slate-500">Allowed</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Info Card */}
          <div className="card-dark p-6 border-cyan-500/20">
            <h4 className="text-sm font-semibold text-cyan-400 mb-2">How it works</h4>
            <ul className="text-xs text-slate-400 space-y-2">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-3 h-3 text-green-400 mt-1 flex-shrink-0" />
                <span>Whitelisted IPs bypass all security checks</span>
              </li>
              <li className="flex items-start gap-2">
                <ShieldOff className="w-3 h-3 text-red-400 mt-1 flex-shrink-0" />
                <span>Blacklisted IPs are immediately blocked</span>
              </li>
              <li className="flex items-start gap-2">
                <Globe className="w-3 h-3 text-cyan-400 mt-1 flex-shrink-0" />
                <span>Country restrictions apply to non-whitelisted IPs</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
      </>}

      {activeTab === "blocked-page" && (
        <BlockedPageWorkspace value={blockedPage} onChange={setBlockedPage} onSave={saveBlockedPage} saving={blockedPageSaving} loading={blockedPageLoading} message={blockedPageMessage} />
      )}
    </div>
  );
};

export default GeoAccess;
